import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ProfitChart = ({ bets, isPrivacyMode, chartType = 'line' }) => {
  // Agrupar apostas por data
  const groupBetsByDate = () => {
    const grouped = {};
    
    bets.forEach(bet => {
      const date = new Date(bet.date).toISOString().split('T')[0];
      if (!grouped[date]) {
        grouped[date] = {
          date,
          profit: 0,
          volume: 0,
          count: 0
        };
      }
      grouped[date].profit += bet.profit || 0;
      grouped[date].volume += bet.stake || 0;
      grouped[date].count += 1;
    });

    // Converter para array e ordenar por data
    return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const dailyData = groupBetsByDate();
  
  // Calcular lucro acumulado
  let accumulated = 0;
  const accumulatedData = dailyData.map(day => {
    accumulated += day.profit;
    return {
      ...day,
      accumulated
    };
  });

  const formatCurrency = (value) => {
    if (isPrivacyMode) return 'R$ •••';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const chartData = {
    labels: accumulatedData.map(day => 
      new Date(day.date).toLocaleDateString('pt-BR', { 
        day: '2-digit',
        month: '2-digit'
      })
    ),
    datasets: [
      {
        label: 'Lucro Diário',
        data: accumulatedData.map(day => isPrivacyMode ? 0 : day.profit),
        borderColor: '#10b981',
        backgroundColor: '#10b98120',
        borderWidth: 2,
        tension: 0.1,
        fill: false,
      },
      {
        label: 'Lucro Acumulado',
        data: accumulatedData.map(day => isPrivacyMode ? 0 : day.accumulated),
        borderColor: '#3b82f6',
        backgroundColor: '#3b82f620',
        borderWidth: 2,
        tension: 0.1,
        fill: true,
        fillColor: '#3b82f610',
      }
    ]
  };

  const barData = {
    labels: accumulatedData.map(day => 
      new Date(day.date).toLocaleDateString('pt-BR', { 
        day: '2-digit',
        month: '2-digit'
      })
    ),
    datasets: [
      {
        label: 'Apostas por Dia',
        data: accumulatedData.map(day => day.count),
        backgroundColor: '#8b5cf6',
        borderColor: '#7c3aed',
        borderWidth: 1,
      },
      {
        label: 'Volume (R$)',
        data: accumulatedData.map(day => isPrivacyMode ? 0 : day.volume),
        backgroundColor: '#f59e0b',
        borderColor: '#d97706',
        borderWidth: 1,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#6b7280',
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: chartType === 'line' ? 'Evolução do Lucro' : 'Volume e Quantidade de Apostas',
        color: '#374151',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (isPrivacyMode) {
              return `${context.dataset.label}: ••••••`;
            }
            if (context.dataset.label.includes('R$') || context.dataset.label.includes('Lucro')) {
              return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
            }
            return `${context.dataset.label}: ${context.parsed.y}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#f3f4f6'
        },
        ticks: {
          color: '#6b7280',
          callback: function(value) {
            if (isPrivacyMode) return '•••';
            if (chartType === 'line') {
              return formatCurrency(value);
            }
            return value;
          }
        }
      },
      x: {
        grid: {
          color: '#f3f4f6'
        },
        ticks: {
          color: '#6b7280'
        }
      }
    }
  };

  if (accumulatedData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <p>Nenhum dado disponível para exibir o gráfico</p>
      </div>
    );
  }

  return (
    <div className="h-64">
      {chartType === 'line' ? (
        <Line data={chartData} options={options} />
      ) : (
        <Bar data={barData} options={options} />
      )}
    </div>
  );
};

export default ProfitChart;